import React from 'react';
import {
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import { formatDateTimeInVenueTz } from '../../../helpers/formatDateTime';
import { useSort } from '../../../hooks/useSort';
import SortableHeaderCell from '../../../components/atom/SortableHeaderCell';
import { getBookingStatusColor } from '../helpers/getBookingStatusColor';
import { aggregateBreakdown } from '../helpers/aggregateCancelledRefundedBreakdown';

const statusChip = (status) => (
    <Chip label={status} size="small" color={getBookingStatusColor(status)} />
);

const AffiliateCancelledRefundedReportTable = ({
    items,
    totalBookings,
    totalOriginalAmount,
    totalRefundedAmount,
    totalProcessingFees,
    showTicketTypeBreakdown,
    showTicketOptionBreakdown,
}) => {
    const { t } = useTranslation();
    const sort = useSort(items, 'cancellationDate', 'desc');

    const fixedColCount =
        7 + (showTicketTypeBreakdown ? 1 : 0) + (showTicketOptionBreakdown ? 1 : 0);

    return (
        <TableContainer sx={{ maxHeight: '65vh' }}>
            <Table stickyHeader sx={{ width: '100%', '& thead th': { top: 0 } }}>
                <TableHead>
                    <TableRow>
                        <SortableHeaderCell field="bookingId" sort={sort}>
                            {t('reports.affiliateCancelledRefunded.bookingId')}
                        </SortableHeaderCell>
                        <SortableHeaderCell field="bookingStatus" sort={sort}>
                            {t('reports.affiliateCancelledRefunded.status')}
                        </SortableHeaderCell>
                        <SortableHeaderCell field="agentName" sort={sort}>
                            {t('reports.affiliateCancelledRefunded.agent')}
                        </SortableHeaderCell>
                        <SortableHeaderCell field="commissionGroupName" sort={sort}>
                            {t('reports.affiliateCancelledRefunded.commissionGroup')}
                        </SortableHeaderCell>
                        <SortableHeaderCell field="cancellationDate" sort={sort}>
                            {t('reports.affiliateCancelledRefunded.cancellationDate')}
                        </SortableHeaderCell>
                        <SortableHeaderCell field="cancellationActor" sort={sort}>
                            {t('reports.affiliateCancelledRefunded.cancellationActor')}
                        </SortableHeaderCell>
                        <TableCell>
                            {t('reports.affiliateCancelledRefunded.cancellationReason')}
                        </TableCell>
                        {showTicketTypeBreakdown && (
                            <TableCell>
                                {t('reports.affiliateCancelledRefunded.ticketType')}
                            </TableCell>
                        )}
                        {showTicketOptionBreakdown && (
                            <TableCell>
                                {t('reports.affiliateCancelledRefunded.ticketOption')}
                            </TableCell>
                        )}
                        <SortableHeaderCell field="originalAmount" sort={sort} align="right">
                            {t('reports.affiliateCancelledRefunded.originalAmount')}
                        </SortableHeaderCell>
                        <SortableHeaderCell field="refundedAmount" sort={sort} align="right">
                            {t('reports.affiliateCancelledRefunded.refundedAmount')}
                        </SortableHeaderCell>
                        <SortableHeaderCell field="processingFees" sort={sort} align="right">
                            {t('reports.affiliateCancelledRefunded.processingFees')}
                        </SortableHeaderCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(sort.sortedItems || []).map((item) => {
                        const breakdownRows = aggregateBreakdown(
                            item.breakdown || [],
                            showTicketTypeBreakdown,
                            showTicketOptionBreakdown,
                        );
                        return (
                            <React.Fragment key={item.bookingId}>
                                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                    <TableCell>
                                        <strong>{item.bookingId}</strong>
                                    </TableCell>
                                    <TableCell>{statusChip(item.bookingStatus)}</TableCell>
                                    <TableCell>{item.agentName ?? '-'}</TableCell>
                                    <TableCell>{item.commissionGroupName ?? '-'}</TableCell>
                                    <TableCell>
                                        {item.cancellationDate
                                            ? formatDateTimeInVenueTz(item.cancellationDate)
                                            : '-'}
                                    </TableCell>
                                    <TableCell>{item.cancellationActor ?? '-'}</TableCell>
                                    <TableCell>{item.cancellationReason ?? '-'}</TableCell>
                                    {showTicketTypeBreakdown && <TableCell />}
                                    {showTicketOptionBreakdown && <TableCell />}
                                    <TableCell align="right">
                                        <strong>{formatCentsToUsd(item.originalAmount)}</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>{formatCentsToUsd(item.refundedAmount)}</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>{formatCentsToUsd(item.processingFees)}</strong>
                                    </TableCell>
                                </TableRow>
                                {breakdownRows.map((bd) => (
                                    <TableRow key={bd.key}>
                                        <TableCell />
                                        <TableCell />
                                        <TableCell />
                                        <TableCell />
                                        <TableCell />
                                        <TableCell />
                                        <TableCell sx={{ color: 'text.secondary' }}>
                                            {`×${bd.itemCount}`}
                                        </TableCell>
                                        {showTicketTypeBreakdown && (
                                            <TableCell sx={{ color: 'text.secondary' }}>
                                                {bd.ticketTypeName}
                                            </TableCell>
                                        )}
                                        {showTicketOptionBreakdown && (
                                            <TableCell sx={{ color: 'text.secondary' }}>
                                                {bd.ticketOptionName}
                                            </TableCell>
                                        )}
                                        <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                            {formatCentsToUsd(bd.totalAmount)}
                                        </TableCell>
                                        <TableCell />
                                        <TableCell />
                                    </TableRow>
                                ))}
                            </React.Fragment>
                        );
                    })}
                    <TableRow sx={{ backgroundColor: 'action.selected' }}>
                        <TableCell colSpan={fixedColCount}>
                            <strong>{t('reports.affiliateCancelledRefunded.grandTotal')}</strong> (
                            {totalBookings})
                        </TableCell>
                        <TableCell align="right">
                            <strong>{formatCentsToUsd(totalOriginalAmount)}</strong>
                        </TableCell>
                        <TableCell align="right">
                            <strong>{formatCentsToUsd(totalRefundedAmount)}</strong>
                        </TableCell>
                        <TableCell align="right">
                            <strong>{formatCentsToUsd(totalProcessingFees)}</strong>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default AffiliateCancelledRefundedReportTable;
